import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, List, ListItemButton, MenuItem, TextField, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { SUPPORT_CATEGORIES } from '../../config/support';
import { SupportService } from '../../services/supportService';
import type { SupportTicketReceipt, SupportTicketSummary } from '../../types';
import SupportTicketConversation from './SupportTicketConversation';
import { TicketStatus, displayDate, STATUS_LABELS } from './supportTicketPresentation';

export default function SupportTicketHistory({ onNewTicket, initialTicketId = null, admin = false }: {
  onNewTicket?: () => void; initialTicketId?: string | null; admin?: boolean;
}) {
  const [tickets, setTickets] = useState<SupportTicketSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ cursor?: string } | null>(null);
  const [selected, setSelected] = useState<string | null>(initialTicketId);
  const sequence = useRef(0);
  const [status, setStatus] = useState<SupportTicketReceipt['status'] | ''>('');
  const load = useCallback(async (cursor?: string) => {
    const request = ++sequence.current;
    setLoading(true); setError(null);
    try {
      const result = await (admin ? SupportService.listAdminTickets(status || undefined, cursor) : SupportService.listTickets(cursor));
      if (sequence.current !== request) return;
      setTickets(previous => cursor ? Array.from(new Map([...previous, ...result.tickets].map(ticket => [ticket.id, ticket])).values()) : result.tickets);
      setNextCursor(result.nextCursor);
    } catch { if (sequence.current === request) setError({ cursor }); }
    finally { if (sequence.current === request) setLoading(false); }
  }, [admin, status]);
  useEffect(() => {
    void load();
    // This is a request generation counter, not a DOM ref. Invalidate whichever
    // request is current when the view closes, including a later refresh.
    const invalidate = () => { sequence.current++; };
    return invalidate;
  }, [load]);

  if (selected) return <SupportTicketConversation key={selected} id={selected} admin={admin} onBack={() => { setSelected(null); void load(); }} />;
  return <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
      <Typography variant="body2" color="text.secondary">{admin ? 'All support requests, newest first.' : 'Your requests, newest first.'}</Typography>
      <Button size="small" startIcon={<Refresh />} disabled={loading} onClick={() => load()}>Refresh tickets</Button>
    </Box>
    {admin && <TextField select size="small" label="Ticket status" value={status} onChange={event => setStatus(event.target.value as typeof status)} sx={{ minWidth: 200, my: 1 }}>
      <MenuItem value="">All statuses</MenuItem>
      {Object.entries(STATUS_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
    </TextField>}
    {error && <Alert severity="error" sx={{ mb: 1 }} action={<Button color="inherit" onClick={() => load(error.cursor)}>Retry</Button>}>
      {admin ? 'We could not load the inbox. Please try again.' : 'We could not load your tickets. Please try again.'}
    </Alert>}
    {!loading && !error && !tickets.length && <Box sx={{ py: 3, textAlign: 'center' }}>
      <Typography variant="subtitle1">{admin && status ? 'No tickets with this status' : 'No tickets yet'}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{admin ? 'Support requests will appear here.' : 'Tickets you submit from this account will appear here.'}</Typography>
      {onNewTicket && <Button variant="outlined" onClick={onNewTicket}>Create a ticket</Button>}
    </Box>}
    <List disablePadding aria-label={admin ? 'Support inbox' : 'My support tickets'} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {tickets.map(ticket => <ListItemButton component="button" key={ticket.id} aria-label={`View ticket ${ticket.id}`} onClick={() => setSelected(ticket.id)}
        sx={{ display: 'block', width: '100%', textAlign: 'left', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>{SUPPORT_CATEGORIES[ticket.category]?.label || 'Support ticket'}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>{ticket.unread && <Chip size="small" color="primary" label="Unread" />}<TicketStatus status={ticket.status} /></Box>
        </Box>
        {admin && <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{ticket.requesterName} / {ticket.requesterEmail}</Typography>}
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere', mb: 0.5 }}>{ticket.descriptionPreview}</Typography>
        <Typography variant="caption" color="text.secondary" display="block">{displayDate(ticket.createdAt)}{ticket.studyName ? ` / ${ticket.studyName}` : ''}</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ overflowWrap: 'anywhere' }}>{ticket.id}</Typography>
      </ListItemButton>)}
    </List>
    {loading && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} aria-label="Loading tickets" /></Box>}
    {nextCursor && !error && <Button fullWidth disabled={loading} sx={{ mt: 1 }} onClick={() => load(nextCursor)}>Load more tickets</Button>}
  </Box>;
}
